/**
 * `entities/taxonomy-field-template` query options.
 *
 * Direct hey-api calls by URL (not in the generated SDK yet), same convention
 * as `entities/taxonomy`.
 */
import { queryOptions } from '@tanstack/react-query';
import { assetsHttp } from '@/lib/api/http-client';
import type { TaxonomyFieldTemplate, TaxonomyFieldTemplateChangeLogEntry } from '../model/types';

const TEMPLATES_URL = '/api/v1/taxonomy-field-templates';

export const TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS = {
  all: ['taxonomy-field-templates'] as const,
  // eslint-disable-next-line i18next/no-literal-string -- React Query cache key
  list: () => [...TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS.all, 'list'] as const,
  changeLog: (id: string) => [...TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS.all, 'change-log', id] as const,
};

export const taxonomyFieldTemplatesQueryOptions = () =>
  queryOptions({
    queryKey: TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS.list(),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({ throwOnError: true, url: TEMPLATES_URL, signal });
      return (response.data as TaxonomyFieldTemplate[] | undefined) ?? [];
    },
  });

/**
 * A template's audit trail, newest first.
 *
 * Templates are deliberately UNVERSIONED (copy-on-add means editing one changes
 * nothing downstream), so this trail carries full before/after snapshots rather
 * than version references — the snapshots are the history.
 */
export const taxonomyFieldTemplateChangeLogQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS.changeLog(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${TEMPLATES_URL}/{id}/change-log`,
        path: { id },
        signal,
      });
      return (response.data as TaxonomyFieldTemplateChangeLogEntry[] | undefined) ?? [];
    },
  });
